import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateFormCompletionName,
} from '@assets/i18n/config';
import {
  FileDocumentObjectTypes,
  FileDocumentTypes,
} from '@interfaces/fileDocument';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import { useFileUpload } from '@providers/fileUploadHook';
import { UserContext } from '@providers/userContextProvider';
import { safeWindowOpen } from '@utils/urlSanitization';
import { Box, Button, CircularProgress, useSnackbar } from 'hephaestus/web';
import { Download, Upload } from 'hephaestus/webIcons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FETCH_FORM_COMPLETIONS } from './manageFormCompletions.queries';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
      templateType
      unsignedUrl
      state
      unsignedDocument {
        id
        downloadUrl
      }
    }
  }
`;

const ARCHIVE_FORM_COMPLETION = gql`
  mutation archiveFormCompletion($input: ArchiveFormCompletionInput!) {
    archiveFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

const DELETE_FILE_DOCUMENT = gql`
  mutation deleteFileDocument($input: DeleteFileDocumentInput!) {
    deleteFileDocument(input: $input) {
      fileDocument {
        id
      }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  onArchive,
  canArchive,
}: {
  formCompletionId: string;
  onClose: () => void;
  onArchive?: () => void;
  canArchive: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { enqueueSnackbar } = useSnackbar();
  const [isArchiving, setIsArchiving] = useState(false);
  const { uploadFile } = useFileUpload();
  const { activeOrganization } = useContext(UserContext);

  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const maxRetries = 3;

  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
  });

  // Handle PDF load error with retry
  const handlePdfError = async () => {
    if (pdfRetryCount < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await refetch();
      setPdfRetryCount((prev) => prev + 1);
    } else {
      enqueueSnackbar(t('workflowCompletions:formPdfLoadError'), {
        variant: 'error',
      });
    }
  };

  // Reset retry count when dialog opens with new form
  useEffect(() => {
    setPdfRetryCount(0);
  }, [formCompletionId]);

  const [archiveFormCompletion] = useMutation(ARCHIVE_FORM_COMPLETION);
  const [deleteFileDocument] = useMutation(DELETE_FILE_DOCUMENT);

  const reuploadFile = async (file: File) => {
    try {
      await uploadFile(file, {
        objectType: FileDocumentObjectTypes.FORM_COMPLETION_UNSIGNED,
        objectId: data.fetchFormCompletion.id,
        sourceId: data.fetchFormCompletion.id,
        fileName: file.name,
        mediaType: file.type,
        sourceType: 'FORM_COMPLETION',
        type: FileDocumentTypes.TEMPLATE,
        permissionType: 'PUBLIC',
        name: file.name,
        organizationId: activeOrganization?.id ?? '',
      });
      if (data.fetchFormCompletion.unsignedDocument?.id) {
        await deleteFileDocument({
          variables: {
            input: {
              fileDocumentId: data.fetchFormCompletion.unsignedDocument.id,
            },
          },
        });
      }
      await refetch();
      enqueueSnackbar(t('workflowCompletions:formReuploaded'), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(t('workflowCompletions:formReuploadError'), {
        variant: 'error',
      });
    }
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await archiveFormCompletion({
        variables: {
          input: {
            formCompletionId,
          },
        },
        refetchQueries: [FETCH_FORM_COMPLETIONS],
      });
      enqueueSnackbar(t('workflowCompletions:formArchived'), {
        variant: 'success',
      });
      onArchive?.();
      onClose();
    } catch {
      enqueueSnackbar(t('workflowCompletions:formArchiveError'), {
        variant: 'error',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formCompletion = data?.fetchFormCompletion;

  if (!formCompletion) {
    return null;
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {translateFormCompletionName(formCompletion)}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {formCompletion.templateType === 'PDF' &&
            (formCompletion.unsignedDocument?.downloadUrl ||
              formCompletion.unsignedUrl) && (
              <Box
                sx={{
                  width: '100%',
                }}
              >
                <PdfViewer
                  pdfUrl={
                    formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl
                  }
                  fullWidth
                  onError={handlePdfError}
                />
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {formCompletion.state !== 'ARCHIVED' && canArchive && (
            <Button
              variant='contained'
              color='error'
              onClick={handleArchive}
              disabled={isArchiving}
            >
              {t('shared:archive')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              variant='contained'
              startIcon={<Download />}
              onClick={() =>
                safeWindowOpen(
                  formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl,
                  {
                    target: '_blank',
                    onError: (errorMsg) => {
                      enqueueSnackbar(errorMsg, { variant: 'error' });
                    },
                  },
                )
              }
              color='secondary'
            >
              {t('shared:download')}
            </Button>
            <Button
              startIcon={<Upload />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => {
                  const f = (e.target as HTMLInputElement).files?.[0];
                  if (f) {
                    reuploadFile(f);
                  }
                };
                input.click();
              }}
            >
              {t('shared:reupload')}
            </Button>
          </Box>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
